import {
  createStudioCapabilityRegistry,
} from "./StudioCapabilityRegistry";
import {
  assertStudioPlugin,
} from "./StudioPlugin";
import {
  StudioPluginRegistryError,
} from "./errors";

function assertPluginId(pluginId) {
  if (typeof pluginId !== "string" || pluginId.trim() === "") {
    throw new StudioPluginRegistryError(
      "pluginId must be a non-empty string.",
      "INVALID_PLUGIN_ID"
    );
  }

  return pluginId.trim();
}

export function createStudioPluginRegistry({
  capabilityRegistry = createStudioCapabilityRegistry(),
} = {}) {
  if (
    !capabilityRegistry
    || typeof capabilityRegistry.register !== "function"
    || typeof capabilityRegistry.unregisterPlugin !== "function"
  ) {
    throw new StudioPluginRegistryError(
      "capabilityRegistry must be a valid StudioCapabilityRegistry.",
      "INVALID_CAPABILITY_REGISTRY"
    );
  }

  const plugins = new Map();

  function assertRequirementsAvailable(plugin) {
    const missingRequirements = plugin.requires.filter(
      (capabilityId) => !capabilityRegistry.has(capabilityId)
    );

    if (missingRequirements.length > 0) {
      throw new StudioPluginRegistryError(
        `Studio plugin "${plugin.id}" requires unavailable capabilities: ${missingRequirements.join(", ")}.`,
        "MISSING_PLUGIN_REQUIREMENTS"
      );
    }
  }

  function register(plugin) {
    assertStudioPlugin(plugin);

    if (plugins.has(plugin.id)) {
      throw new StudioPluginRegistryError(
        `Studio plugin "${plugin.id}" is already registered.`,
        "DUPLICATE_PLUGIN_ID"
      );
    }

    assertRequirementsAvailable(plugin);

    const registeredCapabilities = [];

    try {
      plugin.capabilities.forEach((capability) => {
        capabilityRegistry.register(plugin.id, capability);
        registeredCapabilities.push(capability.id);
      });

      plugins.set(plugin.id, plugin);

      return plugin;
    } catch (error) {
      registeredCapabilities.forEach((capabilityId) => {
        capabilityRegistry.unregister(plugin.id, capabilityId);
      });

      throw error;
    }
  }

  function unregister(pluginId) {
    const normalizedPluginId = assertPluginId(pluginId);
    const plugin = plugins.get(normalizedPluginId);

    if (!plugin) {
      return false;
    }

    const dependants = [...plugins.values()]
      .filter((candidate) => candidate.id !== normalizedPluginId)
      .filter((candidate) => candidate.requires.some(
        (capabilityId) => plugin.capabilities.some(
          (capability) => capability.id === capabilityId
        )
      ))
      .map((candidate) => candidate.id)
      .sort();

    if (dependants.length > 0) {
      throw new StudioPluginRegistryError(
        `Studio plugin "${normalizedPluginId}" is required by: ${dependants.join(", ")}.`,
        "PLUGIN_HAS_DEPENDANTS"
      );
    }

    capabilityRegistry.unregisterPlugin(normalizedPluginId);
    plugins.delete(normalizedPluginId);

    return true;
  }

  function get(pluginId) {
    return plugins.get(assertPluginId(pluginId)) || null;
  }

  function has(pluginId) {
    return plugins.has(assertPluginId(pluginId));
  }

  function list() {
    return Object.freeze(
      [...plugins.values()].sort(
        (left, right) => left.id.localeCompare(right.id)
      )
    );
  }

  function clear() {
    const removedCount = plugins.size;

    plugins.clear();
    capabilityRegistry.clear();

    return removedCount;
  }

  return Object.freeze({
    register,
    unregister,
    get,
    has,
    list,
    clear,
    capabilityRegistry,
  });
}
